import React, { createContext, useCallback, useContext, useState } from 'react';

/**
 * Global Sign-In context
 *
 * Exposes:
 * - isOpen: whether the global SignInDrawer is visible
 * - defaultToLogin: whether the drawer should open directly in login mode
 * - fullScreenOnMobile: whether the drawer should cover the full screen on mobile
 * - openSignIn(options): open the drawer (idempotent)
 * - closeSignIn(): close the drawer
 */

const SignInContext = createContext({
  isOpen: false,
  defaultToLogin: false,
  fullScreenOnMobile: false,
  // eslint-disable-next-line no-unused-vars
  openSignIn: (_options = {}) => {},
  closeSignIn: () => {},
});

export const SignInProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [defaultToLogin, setDefaultToLogin] = useState(false);
  const [fullScreenOnMobile, setFullScreenOnMobile] = useState(false);
  const [modeVersion, setModeVersion] = useState(0);

  const openSignIn = useCallback(
    options => {
      const {
        defaultToLogin: nextDefaultToLogin = false,
        fullScreenOnMobile: nextFullScreen = false,
      } = options || {};

      if (isOpen) {
        // Drawer already open: allow switching between login mode or layout
        setDefaultToLogin(!!nextDefaultToLogin);
        setFullScreenOnMobile(!!nextFullScreen);
        setModeVersion(v => v + 1);
        return;
      }

      setDefaultToLogin(!!nextDefaultToLogin);
      setFullScreenOnMobile(!!nextFullScreen);
      setIsOpen(true);
      setModeVersion(v => v + 1);
    },
    [isOpen]
  );

  const closeSignIn = useCallback(() => {
    setIsOpen(false);
  }, []);

  const value = {
    isOpen,
    defaultToLogin,
    fullScreenOnMobile,
    modeVersion,
    openSignIn,
    closeSignIn,
  };

  return <SignInContext.Provider value={value}>{children}</SignInContext.Provider>;
};

export const useSignInContext = () => useContext(SignInContext);

export default SignInContext;
